import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, forkJoin, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface ScdUser {
    gid: string;
    firstName: string;
    surName: string;
    mail: string;
    /**
     * The person's **primary organisation code** — shown beside a search result in the picker (#187).
     * SCD's `GET /v2/persons` records carry the org under the `department` key, which can be a single
     * code OR an array of org-hierarchy levels (e.g. `["SHS","HR","PC"]`) ordered most-significant
     * first. Only the PRIMARY (top-level, first) code is kept here (#263 maintainer request — the full
     * hierarchy chain is noise in the picker). Left `undefined` when absent — the UI omits it rather
     * than rendering "null".
     */
    orgCode?: string;
}

/** Explicit per-field search criteria for the member picker (#187). At least one must be non-empty. */
export interface ScdSearchCriteria {
    firstname?: string;
    surname?: string;
    mail?: string;
}

/** Raw SCD person record as returned by `/v2/persons` — a superset of {@link ScdUser}. */
interface ScdPersonRecord {
    gid: string;
    firstName: string;
    surName: string;
    mail: string;
    /**
     * The SCD org/department (confirmed by the maintainer as the `department` field). SCD returns it
     * either as a single string or as an array of org-hierarchy levels ordered most-significant first
     * (e.g. `["SHS","HR","PC"]`). Only the PRIMARY (first) level is extracted in {@link toScdUser}
     * (#263) — the full chain is not displayed.
     */
    department?: string | string[];
}

/**
 * Extract the PRIMARY org code from a possibly-array `department`. SCD orders the org-hierarchy array
 * most-significant first, so the primary code is the FIRST non-empty level (a plain string is its own
 * primary). Returns `undefined` when there is no non-empty level (#263 — only the primary is shown,
 * not the joined hierarchy chain).
 */
const primaryOrgCode = (department?: string | string[]): string | undefined => {
    const levels = (Array.isArray(department) ? department : [department ?? '']).map(part => (part ?? '').trim());
    return levels.find(Boolean) || undefined;
};

/** Normalise a raw SCD record into our {@link ScdUser}. Only the PRIMARY org code (first `department`
 *  level) is kept — #263. */
const toScdUser = (r: ScdPersonRecord): ScdUser => ({
    gid: r.gid,
    firstName: r.firstName,
    surName: r.surName,
    mail: r.mail,
    orgCode: primaryOrgCode(r.department)
});

/** Canonical identity for a GID — trimmed and upper-cased so case/whitespace variants collapse. */
const normaliseGid = (gid: string): string => gid.trim().toUpperCase();

/**
 * Collapse records that share a GID into one entry. SCD's `/v2/persons` can return the SAME person as
 * several records when more than one query field matches (e.g. a firstname+surname search returns a
 * row keyed on the name match and another on the mail match), and those duplicates differ only in which
 * optional fields are populated — sometimes even in GID casing/whitespace. Since a membership is keyed
 * on the GID alone, two records with the SAME (normalised) GID are the same person — rendering both as
 * separate pickable rows is wrong (both highlight on select). Key on the normalised GID so variants
 * collapse, merge in first-seen order, fill a missing `mail`, and keep the FIRST non-empty PRIMARY org
 * code (#263: only the primary org is shown — a union across duplicate records would re-create a
 * multi-code string, which is exactly what the primary-only rule removes).
 */
const dedupeByGid = (users: ScdUser[]): ScdUser[] => {
    const byGid = new Map<string, ScdUser>();
    for (const user of users) {
        if (!user?.gid) {
            continue;
        }
        const key = normaliseGid(user.gid);
        const existing = byGid.get(key);
        if (!existing) {
            byGid.set(key, { ...user });
            continue;
        }
        existing.mail ||= user.mail;
        // Keep the first-seen non-empty primary org code; a later duplicate only fills an absent one.
        existing.orgCode ||= user.orgCode;
    }
    return [...byGid.values()];
};

/** Locate the SCD entry in apiConfig by its scope prefix — avoids fragile array-index coupling. */
const scdConfig = environment.apiConfig.find(c => c.scopes.some(s => s.includes('scd.suites.siemens')));
if (!scdConfig) {
    throw new Error('SCD apiConfig entry not found. Check environment.apiConfig.');
}

@Injectable({ providedIn: 'root' })
export class ScdApiService {
    private readonly http = inject(HttpClient);
    /** Base URL without trailing `/*` — MsalInterceptor matches on the full URI pattern. */
    private readonly baseUrl = scdConfig?.uri.replace('/*', '');
    private readonly apiKey: string = scdConfig?.apiKey ? String(scdConfig.apiKey) : '';

    /**
     * Look up a single user by GID via the SCD v2 API.
     * The MsalInterceptor automatically attaches a `SCD.Read` bearer token for this URL.
     * Returns `null` when the user is not found or the request fails.
     */
    public getUserByGid(gid: string): Observable<ScdUser | null> {
        const headers = this.apiKey ? new HttpHeaders({ 'x-api-key': this.apiKey }) : new HttpHeaders();
        return this.http
            .get<{ records: ScdPersonRecord[] }>(`${this.baseUrl}/v2/persons`, { params: { gid }, headers })
            .pipe(map(response => (response.records?.[0] ? toScdUser(response.records[0]) : null)));
    }

    /**
     * Searches SCD people by free-text term against the same `GET /v2/persons` endpoint used by
     * {@link getUserByGid}. The backend `SCDQuery` DTO already accepts `firstname` / `surname` /
     * `mail` (no backend change — #187 member-search), so a single typed term is fanned out across
     * those three fields and the de-duplicated union is returned. This replaces the raw-GID add-member
     * input with a name/mail typeahead.
     *
     * Each matched field is a separate request because the SCD endpoint ANDs the query params it is
     * given (passing all three at once would require a person to match firstname AND surname AND mail
     * simultaneously). The three result sets are merged and de-duplicated by GID. A failed field query
     * degrades to an empty contribution rather than failing the whole search, so a partial SCD outage
     * still returns whatever matched.
     *
     * Returns at most `limit` users (default 10) so the typeahead overlay stays bounded.
     */
    public searchUsers(term: string, limit = 10): Observable<ScdUser[]> {
        const query = term.trim();
        if (!query) {
            return of([]);
        }
        const headers = this.apiKey ? new HttpHeaders({ 'x-api-key': this.apiKey }) : new HttpHeaders();
        const byField = (field: 'firstname' | 'surname' | 'mail'): Observable<ScdUser[]> =>
            this.http
                .get<{ records: ScdPersonRecord[] }>(`${this.baseUrl}/v2/persons`, {
                    params: { [field]: query },
                    headers
                })
                .pipe(
                    map(response => (response.records ?? []).map(toScdUser)),
                    // A single-field outage must not fail the whole search — contribute nothing instead.
                    catchError(() => of<ScdUser[]>([]))
                );
        return forkJoin([byField('firstname'), byField('surname'), byField('mail')]).pipe(
            map(([byFirst, bySur, byMail]) => {
                // Merge + de-duplicate by the NORMALISED GID (a person matching on more than one field
                // appears once), preserving first-seen order, and cap to keep the overlay bounded. SCD
                // can hand back the same GID with case/whitespace drift, so key on normaliseGid — a raw
                // key would let those variants survive as duplicate rows (matching dedupeByGid).
                const seen = new Set<string>();
                const merged: ScdUser[] = [];
                for (const user of [...byFirst, ...bySur, ...byMail]) {
                    if (!user?.gid) {
                        continue;
                    }
                    const key = normaliseGid(user.gid);
                    if (!seen.has(key)) {
                        seen.add(key);
                        merged.push(user);
                    }
                }
                return merged.slice(0, limit);
            })
        );
    }

    /**
     * Explicit multi-field people search for the add-member picker (#187). Unlike {@link searchUsers}
     * (which fans ONE term across firstname/surname/mail and UNIONs the results), this sends the
     * caller's distinct firstname/surname/mail criteria as separate query params on a SINGLE request —
     * the SCD endpoint ANDs them, so each supplied field NARROWS the result set (e.g. firstname "Max"
     * AND surname "Kreis"). Empty/whitespace criteria are omitted. Returns `[]` when no criterion is
     * given rather than listing the whole directory.
     *
     * Returns at most `limit` users (default 25) so the result list stays bounded.
     */
    public searchPeople(criteria: ScdSearchCriteria, limit = 25): Observable<ScdUser[]> {
        const params: Record<string, string> = {};
        const firstname = criteria.firstname?.trim();
        const surname = criteria.surname?.trim();
        const mail = criteria.mail?.trim();
        if (firstname) {
            params['firstname'] = firstname;
        }
        if (surname) {
            params['surname'] = surname;
        }
        if (mail) {
            params['mail'] = mail;
        }
        if (Object.keys(params).length === 0) {
            return of([]);
        }
        const headers = this.apiKey ? new HttpHeaders({ 'x-api-key': this.apiKey }) : new HttpHeaders();
        return this.http.get<{ records: ScdPersonRecord[] }>(`${this.baseUrl}/v2/persons`, { params, headers }).pipe(
            map(response => dedupeByGid((response.records ?? []).map(toScdUser)).slice(0, limit)),
            catchError(() => of<ScdUser[]>([]))
        );
    }
}
